import * as React from "react";
import {FC, useEffect, useState} from "react";
import {ChessPiece} from "../models/chessPiece";
import {Grid, Letter} from "../models/grid";
import "./InteractiveGrid.scss";
import {useDrag, useDrop} from "react-dnd";
import {getEmptyImage} from "react-dnd-html5-backend";
import clsx from "clsx";
import {isPieceMoveValid} from "../services/moveService";
import {DndProvider} from "react-dnd-multi-backend";
import {HTML5toTouch} from "rdndmb-html5-to-touch";
import {Puzzle} from "../models/puzzle";
import {PieceDragLayer} from "./PieceDragLayer";
import {pieceIcon} from "./pieceIcons";

export type DraggableLetterProps = {
    letter: Letter;
    availableOption?: boolean;
    pos: [number, number];
};

export const DraggableLetter: FC<DraggableLetterProps> = ({
                                                              letter,
                                                              pos,
                                                              availableOption,
                                                          }) => {
    const [{isDragging}, drag, preview] = useDrag({
        type: "ChessPiece",
        item: pos,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    // Hide the default (broken) drag image — the PieceDragLayer renders the
    // lifted tile with its chess-piece badge instead.
    useEffect(() => {
        preview(getEmptyImage(), {captureDraggingState: true});
    }, [preview]);
    if (letter) {
        return (
            <div
                className={clsx("letter", {
                    "letter--dragging": isDragging,
                    "letter--option": availableOption,
                })}
                ref={drag}
            >
                {letter}
            </div>
        );
    }
    return (
        <div
            className={clsx("letter", "letter-blank", {
                "letter--option": availableOption,
            })}
        >
            {letter}
        </div>
    );
};

export type InteractiveCellProps = {
    state: Grid;
    gridRowIndex: number;
    gridColIndex: number;
    gridCell: Letter;
    currentPiece: ChessPiece;
    // Utilized to force re-rendering as necessary
    moveNumber: number;
    onDrop: (fromPos: [number, number], toPos: [number, number]) => void;
    clueNumber?: string;
    error?: boolean;
    correct?: boolean;
    won?: boolean;
    winDelay?: number;
    popped?: boolean;
};
export const InteractiveCell: FC<InteractiveCellProps> = ({
                                                              state,
                                                              currentPiece,
                                                              clueNumber,
                                                              gridRowIndex,
                                                              gridColIndex,
                                                              moveNumber,
                                                              gridCell,
                                                              onDrop,
    error,
    correct,
    won,
    winDelay,
    popped,
                                                          }) => {
    const [{isOver, canDrop}, drop] = useDrop(
        () => ({
            accept: "ChessPiece",
            canDrop: (item: [number, number]) =>
                isPieceMoveValid(
                    state,
                    currentPiece,
                    item[0],
                    item[1],
                    gridColIndex,
                    gridRowIndex
                ),
            drop: (item: [number, number]) =>
                onDrop(item, [gridColIndex, gridRowIndex]),
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop(),
            }),
        }),
        [gridRowIndex, gridColIndex, currentPiece, moveNumber]
    );
    const hintIcon = pieceIcon(currentPiece, canDrop && isOver);
    return (
        <div
            ref={drop}
            className={clsx("chessBoard-space", {
                "chessBoard-space--option": canDrop,
                "chessBoard-space--target": canDrop && isOver,
            })}
        >
            {canDrop && hintIcon && (
                <img
                    className={clsx("chessBoard-space--hint", {
                        "chessBoard-space--hint-active": isOver,
                    })}
                    src={hintIcon}
                    alt=""
                    aria-hidden="true"
                />
            )}
            <div
                className={clsx("chessBoard-space-inner", {
                    "chessBoard-space-inner--error": error,
                    "chessBoard-space-inner--correct": correct,
                    "chessBoard-space-inner--won": won,
                    "chessBoard-space-inner--pop": popped,
                })}
                style={won ? { animationDelay: `${(winDelay ?? 0) * 0.05}s` } : undefined}
            >
            {clueNumber && <div className="chessBoard-space--clue">{clueNumber}</div>}
            <DraggableLetter letter={gridCell} pos={[gridColIndex, gridRowIndex]}/>
            </div>
        </div>
    );
};

export type InteractiveGridProps = {
    state: Grid;
    onChange: (newState: Grid) => void;
    pieces: ChessPiece[];
    currentPieceIndex: number;
    puzzle: Puzzle;
    success?: boolean;
};

export const InteractiveGrid: FC<InteractiveGridProps> = ({
                                                              state,
                                                              puzzle,
                                                              onChange,
                                                              pieces,
                                                              currentPieceIndex,
                                                              success,
                                                          }) => {
    // The cell a letter most recently landed in — drives the "landing pop".
    const [justPlaced, setJustPlaced] = useState<[number, number] | null>(null);
    const handleChange = (fromPos: [number, number], toPos: [number, number]) => {
        const newState = state.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
                if (rowIndex === fromPos[1] && colIndex === fromPos[0]) {
                    return null;
                }
                if (rowIndex === toPos[1] && colIndex === toPos[0]) {
                    return state[fromPos[1]][fromPos[0]];
                }
                return cell;
            })
        );
        setJustPlaced(toPos);
        onChange(Array.from(newState) as Grid);
    };
    const allPlayed = currentPieceIndex === pieces.length;
    return (
        <DndProvider options={HTML5toTouch}>
            <PieceDragLayer state={state} piece={pieces[currentPieceIndex]}/>
            <div className={clsx('chessBoardWrap', {'chessBoardWrap--won': !!success})}>
            <div className={clsx('chessBoard', `chessBoard-${puzzle.initialState.length}`)}>
                {state.map((gridRow, y) => (
                    <div key={y} className={"chessBoard-row"}>
                        {gridRow.map((gridCell, x) => {
                            let clueNumber: number[] = [];
                            for (let i = 0; i < puzzle.clues.length; i++) {
                                const [num, word] = puzzle.clues[i];
                                if (word.x === x && word.y === y) {
                                    clueNumber.push(num);
                                }
                            }
                            return (
                                <InteractiveCell
                                    key={x}
                                    state={state}
                                    gridRowIndex={y}
                                    gridColIndex={x}
                                    currentPiece={pieces[currentPieceIndex]}
                                    moveNumber={currentPieceIndex}
                                    gridCell={gridCell}
                                    clueNumber={clueNumber.join(', ')}
                                    onDrop={handleChange}
                                    error={allPlayed && !success && state[y][x] !== puzzle.finalState[y][x]}
                                    correct={allPlayed && !success && puzzle.finalState[y][x] !== null && state[y][x] === puzzle.finalState[y][x]}
                                    won={!!success}
                                    winDelay={x + y}
                                    popped={!success && !!justPlaced && justPlaced[0] === x && justPlaced[1] === y}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
            </div>
        </DndProvider>
    );
};
